// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { EVotingStatus } from "@sequentech/ui-core";
/**
 * Whether a channel counts for the seal (`deadline.rs`'s `counts`): enabled,
 * open or paused, or ever started.
 */
var counts = function (_a) {
    var enabled = _a.enabled, status = _a.status, firstStartedAt = _a.firstStartedAt;
    return enabled || status === EVotingStatus.OPEN || status === EVotingStatus.PAUSED || !!firstStartedAt;
};
/**
 * Where voting at an election stands for its seal (VOTE-FREEZE). Mirrors
 * windmill's `seal_deadline`: every channel that counts must be CLOSED, and
 * one must count. Nothing is guessed: an enabled channel that never started
 * (early voting included) holds the seal until it is closed.
 */
export var sealProgress = function (channels) {
    var counted = channels.filter(counts);
    var held = counted.filter(function (channel) { return channel.status !== EVotingStatus.CLOSED; });
    return {
        finished: !held.length && counted.length > 0,
        holding: held.filter(function (_a) {
            var enabled = _a.enabled;
            return enabled;
        }).map(function (_a) {
            var channel = _a.channel;
            return channel;
        }),
        notEnabled: held.filter(function (_a) {
            var enabled = _a.enabled;
            return !enabled;
        }).map(function (_a) {
            var channel = _a.channel;
            return channel;
        }),
    };
};
/**
 * What stopping `stopping` does to the seal of an election. The server
 * closes the named channels whether or not the Post enables them (an
 * event-wide Stop closes them on every Post). Without names it closes the
 * event's enabled channels (`resolve_voting_channels`), which an
 * election's channels don't tell: name them with [`eventNamedChannels`];
 * unnamed, nothing is assumed closed, so the text never promises a seal.
 */
export var stopSealOutcome = function (channels, stopping) {
    var after = channels.map(function (channel) {
        return (stopping === null || stopping === void 0 ? void 0 : stopping.includes(channel.channel)) ? __assign(__assign({}, channel), { status: EVotingStatus.CLOSED }) : channel;
    });
    var _a = sealProgress(after), finished = _a.finished, holding = _a.holding, notEnabled = _a.notEnabled;
    return { seals: finished, holding: holding, notEnabled: notEnabled };
};
/** The status and period-dates keys of each channel in an election's status. */
export var CHANNEL_STATUS_KEYS = {
    ONLINE: {
        status: "voting_status",
        dates: "voting_period_dates",
        enabled: "online",
    },
    KIOSK: {
        status: "kiosk_voting_status",
        dates: "kiosk_voting_period_dates",
        enabled: "kiosk",
    },
    EARLY_VOTING: {
        status: "early_voting_status",
        dates: "early_voting_period_dates",
        enabled: "early_voting",
    },
    TELEPHONE: {
        status: "telephone_voting_status",
        dates: "telephone_voting_period_dates",
        enabled: "telephone",
    },
};
var record = function (value) {
    return value && typeof value === "object" ? value : {};
};
/**
 * What a NULL `voting_channels` enables: Online only, as the server reads it
 * (`VotingChannels::default()`).
 */
var DEFAULT_ENABLED = { online: true };
/** The channels of an election, from its `status` and `voting_channels` columns. */
export var electionSealChannels = function (election) {
    var status = record(election.status);
    var enabled = election.voting_channels == null ? DEFAULT_ENABLED : record(election.voting_channels);
    return Object.keys(CHANNEL_STATUS_KEYS).map(function (name) {
        var _a;
        var keys = CHANNEL_STATUS_KEYS[name];
        return {
            channel: name,
            enabled: enabled[keys.enabled] === true,
            status: (_a = status[keys.status]) !== null && _a !== void 0 ? _a : EVotingStatus.NOT_STARTED,
            firstStartedAt: record(status[keys.dates]).first_started_at,
        };
    });
};
/**
 * Whether online voting ran at the election, which is when its grace period
 * applies (`deadline.rs`: ONLINE with a `first_started_at`, enabled or not,
 * since online ballots were cast under it).
 */
export var onlineRan = function (channels) {
    return channels.some(function (channel) {
        return channel.channel === "ONLINE" && !!channel.firstStartedAt;
    });
};
/** Whether no enabled channel of the election has ever opened. */
export var neverOpened = function (channels) {
    // Opened means it has a start date (the server's `never_opened`): a
    // channel closed without ever starting has none.
    return channels.filter(function (channel) { return channel.enabled; }).every(function (channel) { return !channel.firstStartedAt; });
};
/**
 * The channels a status change applies to, as the server resolves them
 * (`resolve_voting_channels`): the ones named, else the event's enabled
 * channels, and every channel when the event enables none (`voting_channels`
 * unset).
 */
export var eventNamedChannels = function (eventChannels, named) {
    return eventChannels
        .filter(function (channel) {
        return named
            ? named.includes(channel.channel)
            : channel.enabled || !eventChannels.some(function (_a) {
                var enabled = _a.enabled;
                return enabled;
            });
    })
        .map(function (_a) {
        var channel = _a.channel;
        return channel;
    });
};
/**
 * The channels an event-wide Start applies, as `election_event_status.rs`
 * does: [`eventNamedChannels`], except those the event already has open.
 * With Seal at close, each Post opens only the ones it enables.
 */
export var eventStartChannels = function (eventChannels, starting) {
    var named = eventNamedChannels(eventChannels, starting);
    return eventChannels
        .filter(function (channel) { return named.includes(channel.channel); })
        .filter(function (channel) { return channel.status !== "OPEN"; })
        .map(function (_a) {
        var channel = _a.channel;
        return channel;
    });
};
/**
 * The channels of `applied` an event-wide Start leaves closed on this
 * election under Seal at close (`manual_post_change`'s `KeptClosed`): an
 * enabled channel that is CLOSED there. A channel the election doesn't
 * enable is [`notEnabledStartChannels`]'s, closed or not. (An election with
 * seals stays closed on every enabled channel; the caller knows its seals.)
 */
export var keptClosedChannels = function (channels, applied) {
    return applied.filter(function (name) {
        return channels.some(function (channel) {
            return channel.channel === name &&
                channel.enabled &&
                channel.status === "CLOSED";
        });
    });
};
/**
 * The channels of `applied` an event-wide Start leaves as they are on this
 * election under Seal at close, because the election doesn't enable them
 * (`manual_post_change`'s `NotEnabled`): they stay Not started there, as
 * with a scheduled Start. The server checks this before the closed check.
 */
export var notEnabledStartChannels = function (channels, applied) {
    return applied.filter(function (name) { return !channels.some(function (channel) { return channel.channel === name && channel.enabled; }); });
};
